import { formatDateForList, formatDateOnlyForList } from '../../utils'
import {
  resolveConfigDisplayLabel,
  resolveOptionDisplayLabel,
} from '../../utils/config-localization'
import { resolveGridSectionHeaderLabel } from '../../utils/grid-section-header.helper'

/**
 * Maps a single field value to its display label using form configuration options
 * (same rules as mapObjectToLabels). Handles number/string mismatch (e.g. 0 vs "0").
 *
 * @param {string} fieldLabel - Configuration element label, e.g. 'Gender'
 * @param {unknown} value - Raw stored value
 * @param {Record<string, Array<{ value: unknown, label: string }>>} configOptions - from getConfigOptions
 * @returns {unknown} Label string if found, otherwise original value
 */
export const mapValueToLabel = (fieldLabel, value, configOptions) => {
  if (value === undefined || value === null) return value
  const options = configOptions?.[fieldLabel]
  if (!Array.isArray(options) || options.length === 0) return value
  const item = options.find(
    o =>
      o.value === value ||
      o.value == value ||
      String(o.value) === String(value)
  )
  return item ? resolveOptionDisplayLabel(item) : value
}

export const mapObjectToLabels = (object, config) => {
  const getLabel = (configField, value) => {
    const item = config[configField]?.find(i => i.value === value)
    return item ? resolveOptionDisplayLabel(item) : value // Return value if label not found
  }

  const mappedobject = {}

  Object.keys(object || {}).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      const value = object[key]

      if (typeof value === 'object' && value !== null && Object.keys(value).length) {
        // Recursively map nested objects
        mappedobject[key] = mapObjectToLabels(value, config)
      } else {
        // Attempt to map the current key to a label if it exists in conf
        mappedobject[key] = getLabel(key, value)
      }
    }
  })

  return mappedobject
}

/**
 * Campo utilizzabile nei grafici statistiche (esclude divider e Sensitive/isMasked).
 * @param {{ type?: string, isMasked?: boolean }} [elem]
 */
export function isChartableStatsField(elem) {
  if (!elem || elem.type === 'divider') return false
  if (elem.isMasked === true) return false
  return true
}

/**
 * @param {object} configuration
 * @param {string} sectionKey
 * @param {string} fieldLabel
 */
export function findConfigurationElement(configuration, sectionKey, fieldLabel) {
  const lower = String(sectionKey ?? '').toLowerCase()
  const sectionName = Object.keys(configuration || {}).find(
    (k) => k.toLowerCase() === lower,
  )
  if (!sectionName) return null
  return configuration[sectionName].elements?.find((e) => e.label === fieldLabel) ?? null
}

/**
 * @param {object} configuration
 * @param {string} target
 * @param {string} fieldName
 */
export function isSensitiveStatsField(configuration, target, fieldName) {
  const elem = findConfigurationElement(configuration, target, fieldName)
  return elem?.isMasked === true
}

/**
 * Modello colonne per Custom Chart: esclude organization e campi Sensitive.
 * @param {object} configuration
 * @param {(label: string) => string} [translate]
 */
export function getChartColumnGroupingModel(configuration, translate) {
  const { columnGroupingModelWOrg } = getColumnsModel(configuration, translate)

  return columnGroupingModelWOrg
    .map((group) => ({
      ...group,
      children: group.children.filter((child) => {
        if (child.field === 'organization') return false
        const elem = findConfigurationElement(configuration, group.groupId, child.field)
        return elem ? isChartableStatsField(elem) : true
      }),
    }))
    .filter((group) => group.children.length > 0)
}

export const getConfigOptions = (configuration, organizations) => {
  let configOptions = {}

  Object.keys(configuration)
    .sort((a, b) => configuration[a].position - configuration[b].position)
    .forEach(section => {
      configuration[section].elements.forEach(elem => {
        if (elem.options.length) {
          configOptions = {
            ...configOptions,
            [elem.label]: elem.options.map((opt) => ({
              ...opt,
              label: resolveOptionDisplayLabel(opt),
            })),
          }
        }
      })
    })

  return {
    ...configOptions
    , organization: organizations?.filter(org => org.value > 0)?.map(org => ({ value: org.value, label: org.label }))
  }
}

export const getColumnsModel = (configuration, translate, stringTranslate) => {
  const t = stringTranslate || translate || (x => x)
  const displayFieldHeader = (elem) => resolveConfigDisplayLabel(elem)
  const displaySectionHeader = (sectionKey) =>
    resolveGridSectionHeaderLabel(sectionKey, configuration, translate)
  let columns = [{
    field                : 'organization'
    , headerName         : t('Report Type')
    , headerClassName    : 'overload-theme--header'
    , valueGetter        : (value, row) => row?.merge_date ? t('Merged') : value
    , rowSpanValueGetter : (value, row) => {
      if (!row) return null
      const currentCrashId = row['Crash id']
      return `${value}-${currentCrashId}`
    }
  }]
  let columnGroupingModel = []

  Object.keys(configuration)
    .sort((a, b) => configuration[a].position - configuration[b].position)
    .forEach(section => {
      let children = []
      configuration[section].elements.forEach(elem => {
        const defaultColumn = {
          field                : elem.label
          , headerName         : displayFieldHeader(elem)
          , headerClassName    : 'overload-theme--header'
          , ...(/\bdate\b/i.test(elem.label) && {
            valueFormatter: (paramsOrValue) => {
              const v = paramsOrValue?.value ?? paramsOrValue
              return /report\s+date/i.test(elem.label) ? formatDateOnlyForList(v) : formatDateForList(v)
            }
          })
        }

        if (section === 'people') {
          columns = [
            ...columns
            , { ...defaultColumn, rowSpanValueGetter: () => null }
          ]
        } else if (section === 'units') {
          columns = [
            ...columns
            , {
              ...defaultColumn
              , rowSpanValueGetter: (value, row) => {
                if (!row) return null
                const currentUnitId = row?.unit?.['Unit id'] ?? row?.['Unit id']
                return `${value}-${currentUnitId}`
              }
            }
          ]
        } else {
          let minWidth

          switch (true) {
            case elem.label === 'Report date':
              minWidth = 100
              break
            case elem.label === 'Crash date':
              minWidth = 160
              break
            case elem.label === 'Crash id':
              minWidth = 140
              break
            default:
              break
          }

          columns = [
            ...columns
            , {
              ...defaultColumn
              , minWidth
              , rowSpanValueGetter: (value, row) => {
                if (!row) return null
                const currentCrashId = row['Crash id']
                return `${value}-${currentCrashId}`
              }
            }
          ]
        }

        children = [...children, { field: elem.label }]
      })
      columnGroupingModel = [...columnGroupingModel, { groupId: section, headerName: displaySectionHeader(section), headerClassName: 'overload-theme--header', children }]
    })

  const columnGroupingModelWOrg = columnGroupingModel
    .map(group => group.groupId === 'crash' ? ({ ...group, children: [...group.children, { field: 'organization' }] }) : group)

  return { columns, columnGroupingModelWOrg }
}
